import React from 'react';
import { getSession } from 'next-auth/react';

import styled from 'styled-components';

import Box from '../atoms/Box/Box';
import { Col, Row } from '../atoms/Grid';
import Text from '../atoms/Text/Text';
import CenterMessage from '@components/organisms/CenterMessage/CenterMessage';

import { localizedMoment } from '@utils/localizedMoment';

import { BillingHistory } from '@shared/interfaces/billing-history';
import DownloadIcon from '@public/icons/download.svg';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { breakpoint } from 'theme';
import moment from 'moment';
import { withTranslation } from 'i18n';

type BillingHistoryPopupProps = {
  billingHistory: BillingHistory[];
  licenseName: string;
  onClose: () => void;
  t: I18nTranslation;
};

function BillingHistoryPopup({
  billingHistory,
  licenseName,
  onClose,
  t: tProp,
}: BillingHistoryPopupProps) {
  const t = key => tProp(`subs.${key}`);
  const format = 'MMM D, YYYY';

  const handleDownloadReceipt = async (receiptUrl: string, orderId: number) => {
    try {
      const session = await getSession();
      const token = session?.accessToken;

      const response = await fetch(receiptUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download receipt');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading receipt:', error);
    }
  };

  return (
    <CenterMessage onClose={onClose}>
      <Box mb={1.5} smMt={1.5}>
        <Text
          smSize={2}
          size={2.25}
          color="cornflowerBlue"
          lineHeight="normal"
          weight="bold"
        >
          {t('billingHistory')}
        </Text>
      </Box>
      <Box>
        <TableHeader>
          <Col xs={false} sm={3}>
            {t('date')}
          </Col>
          <Col xs={false} sm={4}>
            {t('license')}
          </Col>
          <Col xs={false} sm={3}>
            {t('amount')}
          </Col>
          <Col xs={false} sm={2}>
            {t('download')}
          </Col>
        </TableHeader>

        <RowGroup>
          {billingHistory.length === 0 && (
            <Text
              smSize={1}
              size={1.25}
              color="cornflowerBlue"
              lineHeight="normal"
            >
              {t('empty')}
            </Text>
          )}

          {billingHistory
            .sort((a, b) => {
              let aCreatedTime = a.created;
              let bCreatedTime = b.created;

              if (typeof aCreatedTime === 'string') {
                aCreatedTime = new Date(a.created);
              }
              if (typeof bCreatedTime === 'string') {
                bCreatedTime = new Date(b.created);
              }

              return bCreatedTime.getTime() - aCreatedTime.getTime();
            })
            .map(history => {
              const purchasedDateFormat = localizedMoment(
                history.created,
                format
              );

              return (
                <RowItem key={history.id}>
                  <ColDate xs={5} sm={3}>
                    {purchasedDateFormat}
                  </ColDate>
                  <ColSubName xs={12} sm={4}>
                    {licenseName}
                  </ColSubName>
                  <ColPrice xs={5} sm={3}>
                    {`${history.iso} ${history.real_price}`}
                  </ColPrice>
                  <ColIcon xs={2} sm={2}>
                    <DownloadButton
                      onClick={() => handleDownloadReceipt(history.receipt, history.id)}
                      aria-label="Download receipt"
                    >
                      <DownloadIcon />
                    </DownloadButton>
                  </ColIcon>
                </RowItem>
              );
            })}
        </RowGroup>
      </Box>
    </CenterMessage>
  );
}

const TableHeader = styled(Row)`
  margin-bottom: 0.5em;

  & > div {
    font-size: 1.125em;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
`;

const RowGroup = styled(Box)`
  & > div:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.iron};
  }
`;

const RowItem = styled(Row)`
  border-top: 1px solid ${({ theme }) => theme.colors.iron};
  padding-top: 0.75em;
  padding-bottom: 0.75em;

  & > div {
    font-size: 1.125em;
    line-height: 1.5;
  }
`;

const ColSubName = styled(Col)`
  order: 2;

  ${breakpoint.down('ltm')`
    order: 1;
    margin-bottom: 0.25em;
  `}
`;

const ColDate = styled(Col)`
  order: 1;

  ${breakpoint.down('ltm')`
    order: 2;
  `}
`;

const ColPrice = styled(Col)`
  order: 3;
`;

const ColIcon = styled(Col)`
  order: 4;
  text-align: center;

  ${breakpoint.m`
    text-align: right;
  `}
`;

const DownloadButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

export default withTranslation('account')(BillingHistoryPopup);
